import { Lock, Zap } from 'lucide-react';
import { Link } from 'wouter';
import {
  PERSPECTIVE_PATTERN_CONTENT,
  WF_DIMENSION_LABELS,
  computeOverallScore,
  getHpGrade,
} from '../../lib/assessment/content';
import {
  BOTTLENECK_LABELS,
  BOTTLENECK_READOUTS,
  NEXT_TARGET,
  PERSPECTIVE_DASH_DESC,
  WF_READOUTS,
  WF_TENSION,
  getBotScoreInterpretation,
  getHpScoreDescription,
  getNextGradeTarget,
} from '../../lib/dashboard/summaryContent';
import { latestOf, previousOf } from '../../lib/dashboard/overview';
import { routes } from '../../lib/assessment/routes';
import type { FreeResult } from '../../lib/apiClient';
import { Button } from '../ui/button';

/**
 * The Overview tab's **High Performer OS Summary**, copied from production.
 *
 * Production writes this as seven inline branches — one per combination of
 * assessments taken — each with a paid and an unpaid body. All seven are
 * reproduced below, in production's order, with production's headings, copy,
 * ordering and conditions. The blocks they share are components here rather
 * than seven copies of the same JSX; what each branch renders is unchanged.
 *
 * The copy itself is in `lib/dashboard/summaryContent.ts`, extracted from
 * production's source rather than retyped.
 */
export function HpSummary({
  assessments,
  displayName,
}: {
  assessments: FreeResult[];
  /** Production's header line. `null` falls back to the generic title. */
  displayName: string | null;
}) {
  const bottleneck = latestOf(assessments, 'bottleneck');
  const workFit = latestOf(assessments, 'work_fit');
  const perspective = latestOf(assessments, 'perspective');
  const prevBottleneck = previousOf(assessments, 'bottleneck');
  const prevWorkFit = previousOf(assessments, 'work_fit');

  const heading = displayName ?? 'Your High Performer OS Summary';

  return (
    <section
      className="p-6 rounded-xl border border-primary/20 bg-primary/5 space-y-5"
      data-testid="hp-summary"
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
        <Zap className="w-3.5 h-3.5" aria-hidden="true" />
        High Performer OS Summary
      </div>

      {/* Empty state — no assessments yet */}
      {!bottleneck && !workFit && !perspective ? (
        <div className="space-y-4" data-testid="hp-summary-empty">
          <p className="text-2xl font-bold">{heading}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Start with the Bottleneck Assessment to identify the internal pattern most affecting
            your clarity, consistency, recovery, and execution.
          </p>
          <Link
            href={routes.assessment('bottleneck')}
            data-testid="hp-summary-start"
            className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Button className="gap-2" tabIndex={-1}>
              Take the Bottleneck Assessment
            </Button>
          </Link>
        </div>
      ) : null}

      {bottleneck || workFit || perspective ? (
        <div className="space-y-5">
          {/* Name + assessment attribution */}
          <div>
            <p className="text-2xl font-bold">{heading}</p>
            <div className="mt-2 space-y-1" data-testid="hp-summary-attribution">
              {bottleneck ? (
                <p className="text-sm text-muted-foreground">
                  Bottleneck Assessment, {longDate(bottleneck.completedAt)}
                </p>
              ) : null}
              {workFit ? (
                <p className="text-sm text-muted-foreground">
                  Work Fit Assessment, {longDate(workFit.completedAt)}
                </p>
              ) : null}
              {perspective ? (
                <p className="text-sm text-muted-foreground">
                  Perspective Assessment, {longDate(perspective.completedAt)}
                </p>
              ) : null}
            </div>
          </div>

          <Body
            bottleneck={bottleneck}
            workFit={workFit}
            perspective={perspective}
            prevBottleneck={prevBottleneck}
            prevWorkFit={prevWorkFit}
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * Production's seven branches, in production's order. Each condition is written
 * out rather than collapsed, so the branch a customer lands in is readable
 * beside production's.
 */
function Body({
  bottleneck,
  workFit,
  perspective,
  prevBottleneck,
  prevWorkFit,
}: {
  bottleneck: FreeResult | null;
  workFit: FreeResult | null;
  perspective: FreeResult | null;
  prevBottleneck: FreeResult | null;
  prevWorkFit: FreeResult | null;
}) {
  // Bottleneck only
  if (bottleneck && !workFit && !perspective) {
    return <BottleneckBody bottleneck={bottleneck} prev={prevBottleneck} />;
  }

  // Bottleneck + Perspective
  if (bottleneck && !workFit && perspective) {
    return (
      <BottleneckBody bottleneck={bottleneck} prev={prevBottleneck} perspective={perspective} />
    );
  }

  // Work Fit only
  if (!bottleneck && workFit && !perspective) {
    return <WorkFitBody workFit={workFit} prev={prevWorkFit} />;
  }

  // Work Fit + Perspective
  if (!bottleneck && workFit && perspective) {
    return <WorkFitBody workFit={workFit} prev={prevWorkFit} perspective={perspective} />;
  }

  // Perspective only
  if (!bottleneck && !workFit && perspective) {
    return <PerspectiveBody perspective={perspective} />;
  }

  // Bottleneck + Work Fit, and all three
  if (bottleneck && workFit) {
    return (
      <CombinedBody
        bottleneck={bottleneck}
        workFit={workFit}
        perspective={perspective}
        prevBottleneck={prevBottleneck}
        prevWorkFit={prevWorkFit}
      />
    );
  }

  return null;
}

// ── the Bottleneck branches ──────────────────────────────────────────────────

function BottleneckBody({
  bottleneck,
  prev,
  perspective,
}: {
  bottleneck: FreeResult;
  prev: FreeResult | null;
  /** Present in the Bottleneck + Perspective branch. */
  perspective?: FreeResult;
}) {
  const slug = bottleneck.typeSlug ?? '';
  const readout = BOTTLENECK_READOUTS[slug] ?? null;
  const tops = bottleneck.topCategories ?? [];
  const psSlug = perspective?.typeSlug ?? '';
  const psContent = perspective ? (PERSPECTIVE_PATTERN_CONTENT[psSlug] ?? null) : null;

  const lens = perspective ? (
    <p className="text-sm text-muted-foreground leading-relaxed" data-testid="summary-lens">
      {PERSPECTIVE_DASH_DESC[psSlug] ??
        psContent?.cost ??
        `Your interpretation lens is ${perspective.primaryType}.`}
    </p>
  ) : null;

  const attribution = perspective ? (
    <p className="text-xs text-muted-foreground">
      Bottleneck: <Strong>{bottleneck.primaryType}</Strong> &middot; Perspective:{' '}
      <Strong>{perspective.primaryType}</Strong>.
    </p>
  ) : null;

  if (!bottleneck.isUnlocked) {
    return (
      <div className="space-y-5" data-testid="summary-body" data-state="bottleneck-locked">
        <div className="space-y-3">
          <BlockLabel>Pattern Overview</BlockLabel>
          {readout ? (
            <Para>{readout.para}</Para>
          ) : (
            <Para>
              Your internal performance pattern reflects <Strong>{bottleneck.primaryType}</Strong>
              {perspective ? null : <FrictionTail tops={tops} />}.
            </Para>
          )}
          {lens}
          {attribution ?? (
            <p className="text-xs text-muted-foreground">
              Pattern identified: <Strong>{bottleneck.primaryType}</Strong>.
            </p>
          )}
        </div>

        {readout && readout.inPractice.length > 0 ? (
          <Bullets label="What This May Mean in Practice" items={readout.inPractice.slice(0, 2)} />
        ) : null}

        <UnlockPrompt resultId={bottleneck.id} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="summary-body" data-state="bottleneck-unlocked">
      <div className="space-y-3">
        <BlockLabel>Strategic Readout</BlockLabel>
        {readout ? (
          <Para>{readout.para}</Para>
        ) : (
          <Para>
            Your internal performance pattern reflects <Strong>{bottleneck.primaryType}</Strong>
            <FrictionTail tops={tops} />. Your next growth edge is not working harder. It is
            reducing the pattern that makes consistent execution more difficult.
          </Para>
        )}
        {lens}
        {attribution ?? (
          <p className="text-xs text-muted-foreground">
            Pattern identified: <Strong>{bottleneck.primaryType}</Strong>
            {tops.length >= 2 ? (
              <>
                {' '}
                with <Strong>{joinLabels(tops, BOTTLENECK_LABELS, ' and ')}</Strong> as the top
                friction signals
              </>
            ) : null}
            .
          </p>
        )}
      </div>

      {readout ? <Bullets label="What This Means in Practice" items={readout.inPractice} /> : null}

      {prev ? <HpScoreProgress latest={bottleneck} prev={prev} /> : null}

      {perspective && psContent ? (
        <div className="space-y-3">
          <BlockLabel>Interpretation Lens</BlockLabel>
          <Para>{psContent.cost}</Para>
          <p className="text-sm text-muted-foreground leading-relaxed italic border-l-2 border-primary/30 pl-3">
            &quot;{psContent.immediateReframe}&quot;
          </p>
        </div>
      ) : null}

      {tops.length > 0 ? <NextPerformanceTarget slug={slug} topFactor={tops[0]!} /> : null}

      {tops.length > 0 ? (
        <div className="space-y-2">
          <BlockLabel>Recommended Next Focus</BlockLabel>
          <ul className="space-y-1.5">
            {readout ? <Bullet>{readout.focusTip}</Bullet> : null}
            {tops.slice(0, 2).map((b) => (
              <Bullet key={b}>
                Address your {BOTTLENECK_LABELS[b] ?? b} pattern through your full report.
              </Bullet>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ── the Work Fit branches ────────────────────────────────────────────────────

function WorkFitBody({
  workFit,
  prev,
  perspective,
}: {
  workFit: FreeResult;
  prev: FreeResult | null;
  perspective?: FreeResult;
}) {
  const slug = workFit.typeSlug ?? '';
  const readout = WF_READOUTS[slug] ?? null;
  const dims = workFit.topCategories ?? [];
  const psSlug = perspective?.typeSlug ?? '';
  const psContent = perspective ? (PERSPECTIVE_PATTERN_CONTENT[psSlug] ?? null) : null;

  const lens = perspective ? (
    <p className="text-sm text-muted-foreground leading-relaxed" data-testid="summary-lens">
      {PERSPECTIVE_DASH_DESC[psSlug] ??
        psContent?.cost ??
        `Your interpretation lens is ${perspective.primaryType}.`}
    </p>
  ) : null;

  if (!workFit.isUnlocked) {
    return (
      <div className="space-y-5" data-testid="summary-body" data-state="work-fit-locked">
        <div className="space-y-3">
          <BlockLabel>Pattern Overview</BlockLabel>
          {readout ? (
            <Para>{readout.para}</Para>
          ) : (
            <Para>
              Your Work Fit profile reflects <Strong>{workFit.primaryType}</Strong>
              {dims.length >= 2 ? (
                <>
                  , with <Strong>{joinLabels(dims, WF_DIMENSION_LABELS, ' and ')}</Strong> as the
                  key factors shaping how well your environment supports sustained performance
                </>
              ) : null}
              .
            </Para>
          )}
          {lens}
          <p className="text-xs text-muted-foreground">
            Work Fit profile: <Strong>{workFit.primaryType}</Strong>.
          </p>
        </div>

        {readout && readout.inPractice.length > 0 ? (
          <Bullets label="What This May Mean in Practice" items={readout.inPractice.slice(0, 2)} />
        ) : null}

        <UnlockPrompt resultId={workFit.id} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="summary-body" data-state="work-fit-unlocked">
      <div className="space-y-3">
        <BlockLabel>Strategic Readout</BlockLabel>
        {readout ? (
          <Para>{readout.para}</Para>
        ) : (
          <Para>
            Your Work Fit profile reflects <Strong>{workFit.primaryType}</Strong>
            {dims.length >= 2 ? (
              <>
                , with <Strong>{joinLabels(dims, WF_DIMENSION_LABELS, ' and ')}</Strong> as the key
                factors shaping how well your environment supports sustained performance
              </>
            ) : null}
            . The Bottleneck Assessment can help you identify the internal execution pattern your
            environment is most affecting.
          </Para>
        )}
        {lens}
        <p className="text-xs text-muted-foreground">
          Work Fit profile: <Strong>{workFit.primaryType}</Strong>
          {dims.length >= 2 ? (
            <>
              {' '}
              with <Strong>{joinLabels(dims, WF_DIMENSION_LABELS, ' and ')}</Strong> as the key
              factors
            </>
          ) : null}
          .
        </p>
      </div>

      {readout ? <Bullets label="What This Means in Practice" items={readout.inPractice} /> : null}

      {prev ? <WorkFitProgress latest={workFit} prev={prev} /> : null}

      <div className="space-y-2">
        <BlockLabel>Recommended Next Focus</BlockLabel>
        <ul className="space-y-1.5">
          <Bullet>
            Use your Work Fit profile when evaluating your current role or any future opportunity.
          </Bullet>
          <Bullet>
            Take the Bottleneck Assessment to identify the internal execution pattern your
            environment is most affecting.
          </Bullet>
        </ul>
      </div>
    </div>
  );
}

// ── the Perspective-only branch ──────────────────────────────────────────────

function PerspectiveBody({ perspective }: { perspective: FreeResult }) {
  const slug = perspective.typeSlug ?? '';
  const content = PERSPECTIVE_PATTERN_CONTENT[slug] ?? null;

  const attribution = (
    <p className="text-xs text-muted-foreground">
      Perspective pattern: <Strong>{perspective.primaryType}</Strong>
      {content?.coreLens ? (
        <>
          {' '}
          &middot; <span className="italic">&quot;{content.coreLens}&quot;</span>
        </>
      ) : null}
      .
    </p>
  );

  if (!perspective.isUnlocked) {
    return (
      <div className="space-y-5" data-testid="summary-body" data-state="perspective-locked">
        <div className="space-y-3">
          <BlockLabel>Pattern Overview</BlockLabel>
          {content ? (
            <Para>{content.cost}</Para>
          ) : (
            <Para>
              Your interpretation lens is <Strong>{perspective.primaryType}</Strong>. This is the
              pattern your mind uses to interpret pressure, uncertainty, and action.
            </Para>
          )}
          {attribution}
        </div>

        <UnlockPrompt resultId={perspective.id} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="summary-body" data-state="perspective-unlocked">
      <div className="space-y-3">
        <BlockLabel>Interpretation Lens</BlockLabel>
        {content ? (
          <Para>{content.cost}</Para>
        ) : (
          <Para>
            Your interpretation lens is <Strong>{perspective.primaryType}</Strong>. This is the
            pattern your mind uses to interpret pressure, uncertainty, and action.
          </Para>
        )}
        {content ? (
          <p className="text-sm text-muted-foreground leading-relaxed italic border-l-2 border-primary/30 pl-3">
            &quot;{content.immediateReframe}&quot;
          </p>
        ) : null}
        {attribution}
      </div>

      <div className="space-y-2">
        <BlockLabel>Recommended Next Focus</BlockLabel>
        <ul className="space-y-1.5">
          <Bullet>
            Take the Bottleneck Assessment to identify the internal execution pattern creating the
            most friction in your performance.
          </Bullet>
          <Bullet>
            Use your Perspective pattern to notice how you interpret pressure and feedback as you
            work through your next challenge.
          </Bullet>
        </ul>
      </div>
    </div>
  );
}

// ── the combined branches ────────────────────────────────────────────────────

function CombinedBody({
  bottleneck,
  workFit,
  perspective,
  prevBottleneck,
  prevWorkFit,
}: {
  bottleneck: FreeResult;
  workFit: FreeResult;
  perspective: FreeResult | null;
  prevBottleneck: FreeResult | null;
  prevWorkFit: FreeResult | null;
}) {
  const botSlug = bottleneck.typeSlug ?? '';
  const wfSlug = workFit.typeSlug ?? '';
  const botReadout = BOTTLENECK_READOUTS[botSlug] ?? null;
  const wfReadout = WF_READOUTS[wfSlug] ?? null;
  const tensionNote = WF_TENSION[botSlug]?.[wfSlug] ?? null;
  const botTops = bottleneck.topCategories ?? [];
  const wfDims = workFit.topCategories ?? [];
  const anyPaid = bottleneck.isUnlocked || workFit.isUnlocked;

  const psSlug = perspective?.typeSlug ?? '';
  const psContent = perspective ? (PERSPECTIVE_PATTERN_CONTENT[psSlug] ?? null) : null;
  const lens = perspective ? (
    <p className="text-sm text-muted-foreground leading-relaxed" data-testid="summary-lens">
      {PERSPECTIVE_DASH_DESC[psSlug] ??
        psContent?.cost ??
        `Your interpretation lens is ${perspective.primaryType}.`}
    </p>
  ) : null;

  const bottleneckPara = botReadout ? (
    <Para>{botReadout.para}</Para>
  ) : (
    <Para>
      Your internal performance pattern reflects <Strong>{bottleneck.primaryType}</Strong>
      <FrictionTail tops={botTops} />.
    </Para>
  );

  const workFitPara = wfReadout ? (
    <Para>{wfReadout.para}</Para>
  ) : (
    <Para>
      Your Work Fit result is <Strong>{workFit.primaryType}</Strong>
      {wfDims.length >= 2 ? (
        <>
          , with <Strong>{joinLabels(wfDims, WF_DIMENSION_LABELS, ' and ')}</Strong> as the key
          factors
        </>
      ) : null}
      .
    </Para>
  );

  if (!anyPaid) {
    const freeBullets = [
      ...(botReadout?.inPractice.slice(0, 1) ?? []),
      ...(wfReadout?.inPractice.slice(0, 1) ?? []),
    ].slice(0, 3);

    return (
      <div className="space-y-5" data-testid="summary-body" data-state="combined-locked">
        <div className="space-y-3">
          <BlockLabel>Pattern Overview</BlockLabel>
          {bottleneckPara}
          {workFitPara}
          {lens}
          <p className="text-xs text-muted-foreground">
            Bottleneck: <Strong>{bottleneck.primaryType}</Strong> &middot; Work Fit:{' '}
            <Strong>{workFit.primaryType}</Strong>
            {perspective ? (
              <>
                {' '}
                &middot; Perspective: <Strong>{perspective.primaryType}</Strong>
              </>
            ) : null}
            .
          </p>
        </div>

        {freeBullets.length > 0 ? (
          <Bullets label="What This May Mean in Practice" items={freeBullets} />
        ) : null}

        {/* Combined section: production's plural wording. */}
        <UnlockPrompt resultId={bottleneck.id} plural />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="summary-body" data-state="combined-unlocked">
      <div className="space-y-3">
        <BlockLabel>Strategic Readout</BlockLabel>
        {bottleneckPara}
        {workFitPara}
        {tensionNote ? <Para>{tensionNote}</Para> : null}
        {lens}
        <p className="text-xs text-muted-foreground">
          Bottleneck: <Strong>{bottleneck.primaryType}</Strong>
          {botTops.length >= 2 ? <> ({joinLabels(botTops, BOTTLENECK_LABELS, ', ')})</> : null}{' '}
          &middot; Work Fit: <Strong>{workFit.primaryType}</Strong>
          {wfDims.length >= 2 ? <> ({joinLabels(wfDims, WF_DIMENSION_LABELS, ', ')})</> : null}
          {perspective ? (
            <>
              {' '}
              &middot; Perspective: <Strong>{perspective.primaryType}</Strong>
            </>
          ) : null}
          .
        </p>
      </div>

      {botReadout || wfReadout ? (
        <Bullets
          label="What This Means in Practice"
          items={[...(botReadout?.inPractice ?? []), ...(wfReadout?.inPractice ?? [])]}
        />
      ) : null}

      {prevBottleneck ? <HpScoreProgress latest={bottleneck} prev={prevBottleneck} /> : null}

      {perspective && psContent ? (
        <div className="space-y-3">
          <BlockLabel>Interpretation Lens</BlockLabel>
          <Para>{psContent.cost}</Para>
          <p className="text-sm text-muted-foreground leading-relaxed italic border-l-2 border-primary/30 pl-3">
            &quot;{psContent.immediateReframe}&quot;
          </p>
        </div>
      ) : null}

      {botTops.length > 0 ? <NextPerformanceTarget slug={botSlug} topFactor={botTops[0]!} /> : null}

      {prevWorkFit ? <WorkFitProgress latest={workFit} prev={prevWorkFit} /> : null}

      <div className="space-y-2">
        <BlockLabel>Recommended Next Focus</BlockLabel>
        <ul className="space-y-1.5">
          {botReadout ? <Bullet>{botReadout.focusTip}</Bullet> : null}
          <Bullet>
            Use your Work Fit profile when evaluating your current role or any future opportunity.
          </Bullet>
        </ul>
      </div>
    </div>
  );
}

// ── shared blocks ────────────────────────────────────────────────────────────

/** Production's HP Score panel: the three tiles, the shift note, and the deltas. */
function HpScoreProgress({ latest, prev }: { latest: FreeResult; prev: FreeResult }) {
  const currentScore = Math.round(computeOverallScore(latest));
  const prevScore = Math.round(computeOverallScore(prev));
  const delta = currentScore - prevScore;
  const grade = getHpGrade(currentScore);
  const prevGrade = getHpGrade(prevScore);
  const botScore = Math.round(latest.bottleneckScore ?? 0);
  const prevTops = new Set(prev.topCategories ?? []);
  const currentTops = latest.topCategories ?? [];
  const improved = [...prevTops].filter((b) => !currentTops.includes(b));
  const persistent = currentTops.filter((b) => prevTops.has(b));

  return (
    <div className="pt-4 border-t border-primary/20 space-y-4" data-testid="summary-hp-score">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">HP Score</p>
      <Para>
        Your HP Score measures how well your four performance dimensions are working together:
        execution readiness, internal friction, recovery capacity, and clarity. The scale is 0 to
        100. Higher is better. A score of {currentScore} places you in the{' '}
        <Strong>{grade.label}</Strong> range (Grade {grade.grade}). {getHpScoreDescription(grade.grade)}
      </Para>

      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="p-3 rounded-lg bg-muted/40 space-y-1">
          <p className="text-xs text-muted-foreground">Previous</p>
          <p className="font-bold text-lg" data-testid="hp-previous">
            {prevScore}
          </p>
          <p className="text-xs text-muted-foreground">{prevGrade.label}</p>
        </div>
        <div className="p-3 rounded-lg bg-primary/10 space-y-1">
          <p className="text-xs text-muted-foreground">Latest</p>
          <p className="font-bold text-lg text-primary" data-testid="hp-latest">
            {currentScore}
          </p>
          <p className="text-xs text-muted-foreground">{grade.label}</p>
        </div>
        <div className="p-3 rounded-lg bg-muted/40 space-y-1">
          <p className="text-xs text-muted-foreground">Change</p>
          <p
            data-testid="hp-change"
            className={`font-bold text-lg ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-destructive' : ''}`}
          >
            {delta > 0 ? `+${delta}` : delta === 0 ? '0' : String(delta)}
          </p>
        </div>
      </div>

      {delta > 0 ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          A +{delta} shift is movement in the right direction. Your next target is{' '}
          {getNextGradeTarget(currentScore)}.
        </p>
      ) : null}
      {delta < 0 ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          A {delta} shift means the composite score moved down since your last assessment. This can
          reflect increased self-awareness or a more demanding period, not necessarily regression.
          Your next target remains {getNextGradeTarget(currentScore)}.
        </p>
      ) : null}
      {delta === 0 ? (
        <p className="text-xs text-muted-foreground leading-relaxed">
          Your score is holding steady. Your next target is {getNextGradeTarget(currentScore)}.
        </p>
      ) : null}

      <div className="pt-3 border-t border-primary/10 space-y-2">
        <BlockLabel>Bottleneck Score</BlockLabel>
        <p className="text-sm">
          <span className="font-bold text-lg">{botScore}</span>
          <span className="text-muted-foreground"> / 100</span>
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Lower is better. This measures the intensity of your top friction pattern. A score of{' '}
          {botScore} indicates {getBotScoreInterpretation(botScore)} Reducing this score is the
          primary lever for improving your HP Score.
        </p>
      </div>

      {latest.primaryType !== prev.primaryType ? (
        <p className="text-sm text-muted-foreground">
          Your primary pattern shifted from <Strong>{prev.primaryType}</Strong> to{' '}
          <Strong>{latest.primaryType}</Strong>.
        </p>
      ) : null}

      {improved.length > 0 ? (
        <div className="space-y-1.5">
          <BlockLabel>What Is Improving</BlockLabel>
          <ul className="space-y-1">
            {improved.map((b) => (
              <Bullet key={b} tone="improved">
                {BOTTLENECK_LABELS[b] ?? b} is no longer in your top friction areas.
              </Bullet>
            ))}
          </ul>
        </div>
      ) : null}

      {persistent.length > 0 ? (
        <div className="space-y-1.5">
          <BlockLabel>What Still Needs Attention</BlockLabel>
          <ul className="space-y-1">
            {persistent.map((b) => (
              <Bullet key={b}>{BOTTLENECK_LABELS[b] ?? b} remains a top friction area.</Bullet>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Production renders nothing at all when there is no movement to report. */
function WorkFitProgress({ latest, prev }: { latest: FreeResult; prev: FreeResult }) {
  const prevDims = new Set(prev.topCategories ?? []);
  const currentDims = latest.topCategories ?? [];
  const improved = [...prevDims].filter((d) => !currentDims.includes(d));
  const persistent = currentDims.filter((d) => prevDims.has(d));

  if (!improved.length && !persistent.length && latest.primaryType === prev.primaryType) {
    return null;
  }

  return (
    <div className="pt-4 border-t border-primary/20 space-y-4" data-testid="summary-work-fit-progress">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">
        Progress Since Previous Work Fit Assessment
      </p>

      {latest.primaryType !== prev.primaryType ? (
        <p className="text-sm text-muted-foreground">
          Your Work Fit profile shifted from <Strong>{prev.primaryType}</Strong> to{' '}
          <Strong>{latest.primaryType}</Strong>.
        </p>
      ) : null}

      {improved.length > 0 ? (
        <div className="space-y-1.5">
          <BlockLabel>What Is Improving</BlockLabel>
          <ul className="space-y-1">
            {improved.map((d) => (
              <Bullet key={d} tone="improved">
                {WF_DIMENSION_LABELS[d] ?? d} is no longer a top dimension.
              </Bullet>
            ))}
          </ul>
        </div>
      ) : null}

      {persistent.length > 0 ? (
        <div className="space-y-1.5">
          <BlockLabel>What Still Needs Attention</BlockLabel>
          <ul className="space-y-1">
            {persistent.map((d) => (
              <Bullet key={d}>{WF_DIMENSION_LABELS[d] ?? d} remains a top dimension.</Bullet>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function NextPerformanceTarget({ slug, topFactor }: { slug: string; topFactor: string }) {
  const targetFn = NEXT_TARGET[slug];
  if (!targetFn) return null;

  return (
    <div className="pt-4 border-t border-primary/20 space-y-2" data-testid="summary-next-target">
      <p className="text-xs font-bold uppercase tracking-widest text-primary">
        Next Performance Target
      </p>
      <Para>{targetFn(topFactor)}</Para>
    </div>
  );
}

/**
 * Production writes this prompt two ways, and the difference is the number of
 * assessments the section is summarising: a single-assessment section says
 * "your full report" over **View Full Report**, and a combined one says "your
 * full reports" over **View Full Reports**.
 *
 * V2 used the singular everywhere, so the combined summary — the section that
 * exists precisely because there is more than one — offered to unlock a report,
 * singular. Reproduced properly now.
 *
 * The link is `routes.result(id)` in both, which is V2's accepted unification of
 * production's three per-type result URLs.
 */
function UnlockPrompt({ resultId, plural = false }: { resultId: string; plural?: boolean }) {
  return (
    <div
      className="pt-3 border-t border-primary/20 space-y-3"
      data-testid="summary-unlock"
      data-plural={plural ? 'true' : 'false'}
    >
      <p className="text-xs text-muted-foreground leading-relaxed">
        {plural
          ? 'Unlock your full reports to see the complete strategic readout, HP Score interpretation, and your deeper assessment-specific recommendations.'
          : 'Unlock your full report to see the complete strategic readout, HP Score interpretation, and your deeper assessment-specific recommendations.'}
      </p>
      <Link
        href={routes.result(resultId)}
        data-testid="summary-unlock-link"
        className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Button size="sm" className="text-xs gap-1.5" tabIndex={-1}>
          <Lock className="w-3 h-3" aria-hidden="true" />
          {plural ? 'View Full Reports' : 'View Full Report'}
        </Button>
      </Link>
    </div>
  );
}

// ── primitives ───────────────────────────────────────────────────────────────

const BlockLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{children}</p>
);

const Para = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
);

const Strong = ({ children }: { children: React.ReactNode }) => (
  <span className="text-foreground font-medium">{children}</span>
);

const Bullet = ({
  children,
  tone = 'default',
}: {
  children: React.ReactNode;
  tone?: 'default' | 'improved';
}) => (
  <li className="text-sm text-muted-foreground flex items-start gap-2">
    <span className={`${tone === 'improved' ? 'text-emerald-400' : 'text-primary'} mt-0.5 shrink-0`}>
      -
    </span>
    {children}
  </li>
);

function Bullets({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="space-y-2">
      <BlockLabel>{label}</BlockLabel>
      <ul className="space-y-1.5">
        {items.map((bullet, i) => (
          <Bullet key={i}>{bullet}</Bullet>
        ))}
      </ul>
    </div>
  );
}

/** Production's ", with X and Y as the strongest sources of internal friction". */
function FrictionTail({ tops }: { tops: string[] }) {
  if (tops.length < 2) return null;
  return (
    <>
      , with <Strong>{joinLabels(tops, BOTTLENECK_LABELS, ' and ')}</Strong> as the strongest
      sources of internal friction
    </>
  );
}

const joinLabels = (keys: string[], labels: Record<string, string>, separator: string): string =>
  keys
    .slice(0, 2)
    .map((k) => labels[k] ?? k)
    .join(separator);

function longDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
